import { googleAuthorizeUrl, random, type SignInResult, safeNextPath, signInClient, signInErrorMessage } from "@serophin/shared";
import { type MutableRef, useEffect, useRef, useState } from "preact/hooks";
import { Spinner } from "../components/Spinner";
import { GoogleIcon } from "../icons";

export type GoogleSignInConfig = { clientId: string; redirectUri: string };

export type GoogleSignInButtonProps = GoogleSignInConfig & {
  next?: string;
  busy?: boolean;
  class?: string;
};

export type UseGoogleRedirectSignInOptions = {
  redirectUri: string | undefined;
  onSignedIn: (result: SignInResult, next?: string) => void | Promise<void>;
  onError?: (message: string) => void;
  onCancelled?: () => void;
  baseUrl?: string;
};

type PendingGoogleSignIn = { state: string; next?: string };

const PENDING_KEY = "googleSignIn";

const DEFAULT_GOOGLE_BUTTON_CLASS =
  "flex w-full items-center justify-center gap-3 rounded-lg border border-[#747775] bg-white px-4 py-3 font-medium text-[#1F1F1F] transition hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-transparent dark:bg-black dark:text-white dark:hover:bg-neutral-900";

const savePending = (pending: PendingGoogleSignIn) => {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {}
};

const takePending = (): PendingGoogleSignIn | undefined => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    sessionStorage.removeItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as PendingGoogleSignIn) : undefined;
  } catch {
    return undefined;
  }
};

const takeReturnParams = (): URLSearchParams | undefined => {
  const params = new URLSearchParams(window.location.search);
  if (!params.has("state") || !(params.has("code") || params.has("error"))) {
    return undefined;
  }
  const url = new URL(window.location.href);
  for (const key of ["code", "state", "scope", "authuser", "prompt", "hd", "error"]) {
    url.searchParams.delete(key);
  }
  window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  return params;
};

export const startGoogleRedirectSignIn = ({ clientId, redirectUri, next }: GoogleSignInConfig & { next?: string }): void => {
  const state = random.token(16);
  savePending({ state, ...(next && { next }) });
  window.location.assign(googleAuthorizeUrl({ clientId, redirectUri, state }));
};

type Handlers = MutableRef<Omit<UseGoogleRedirectSignInOptions, "redirectUri">>;

const completeSignIn = async (params: URLSearchParams, redirectUri: string, handlers: Handlers) => {
  const pending = takePending();
  const code = params.get("code");
  if (params.get("error") !== null) {
    if (params.get("error") === "access_denied") {
      handlers.current.onCancelled?.();
      return;
    }
    handlers.current.onError?.("Google sign-in failed. Try again.");
    return;
  }
  if (!code || !pending || pending.state !== params.get("state")) {
    handlers.current.onError?.("Google sign-in failed. Try again.");
    return;
  }
  try {
    const result = await signInClient.googleCode(code, redirectUri, { baseUrl: handlers.current.baseUrl });
    await handlers.current.onSignedIn(result, safeNextPath(pending.next));
  } catch (error) {
    handlers.current.onError?.(signInErrorMessage(error, "provider"));
  }
};

export const useGoogleRedirectSignIn = ({ redirectUri, onSignedIn, onError, baseUrl }: UseGoogleRedirectSignInOptions): boolean => {
  const handlers: Handlers = useRef({ onSignedIn, onError, baseUrl });
  handlers.current = { onSignedIn, onError, baseUrl };
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const params = redirectUri ? takeReturnParams() : undefined;
    if (!redirectUri || !params) {
      return;
    }
    setPending(true);
    void completeSignIn(params, redirectUri, handlers).finally(() => setPending(false));
  }, [redirectUri]);

  return pending;
};

export const GoogleSignInButton = ({ clientId, redirectUri, next, busy = false, class: className }: GoogleSignInButtonProps) => {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const reset = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setLeaving(false);
      }
    };
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);

  const handleClick = () => {
    setLeaving(true);
    startGoogleRedirectSignIn({ clientId, redirectUri, ...(next && { next }) });
  };

  const working = busy || leaving;

  return (
    <button type="button" class={className ?? DEFAULT_GOOGLE_BUTTON_CLASS} disabled={working} aria-busy={working} onClick={handleClick}>
      {working ? <Spinner size={20} /> : <GoogleIcon />}
      Continue with Google
    </button>
  );
};
